import React from 'react';
import { Grid, Typography, makeStyles } from '@material-ui/core';
import {
  Content,
  InfoCard,
  Link,
} from '@backstage/core-components';
import {
  HomePageStarredEntities,
} from '@backstage/plugin-home';
import { AnnouncementBannerWithEmptyState } from '../announcements';
import { BackstageTheme } from '@backstage/theme';

const useStyles = makeStyles((theme: BackstageTheme) => ({
  root: {
    padding: theme.spacing(3),
  },
  header: {
    marginBottom: theme.spacing(4),
    textAlign: 'center',
    padding: theme.spacing(4),
    backgroundColor: theme.palette.background.paper,
    borderRadius: 16,
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
    position: 'relative',
    overflow: 'hidden',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      height: 4,
      background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
    },
    [theme.breakpoints.up('md')]: {
      textAlign: 'left',
    },
  },
  logoContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
    [theme.breakpoints.up('md')]: {
      flexDirection: 'row',
      justifyContent: 'flex-start',
      alignItems: 'center',
    },
  },
  logo: {
    width: '64px',
    height: 'auto',
    marginBottom: theme.spacing(2),
    filter: 'drop-shadow(0 2px 8px rgba(0, 0, 0, 0.15))',
    [theme.breakpoints.up('md')]: {
      width: '72px',
      marginBottom: 0,
      marginRight: theme.spacing(3),
    },
  },
  title: {
    fontWeight: 700,
    fontSize: '2.2rem',
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(0.5),
    letterSpacing: '-0.02em',
    [theme.breakpoints.up('md')]: {
      fontSize: '2.4rem',
      marginBottom: theme.spacing(1),
    },
  },
  subtitle: {
    fontSize: '1.1rem',
    color: theme.palette.text.secondary,
    fontWeight: 400,
    maxWidth: 600,
    margin: '0 auto',
  },
  card: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  cardContent: {
    flexGrow: 1,
  },
  quickActions: {
    marginTop: theme.spacing(3),
  },
  actionLink: {
    display: 'block',
    padding: theme.spacing(1.5),
    margin: theme.spacing(0.5, 0),
    borderRadius: 8,
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    textDecoration: 'none',
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
      transform: 'translateX(4px)',
    },
  },
}));

export const Dashboard: React.FC = () => {
  const classes = useStyles();

  return (
    <div>
      <Content className={classes.root}>
        {/* Header */}
        <Grid container spacing={4}>
          <Grid item xs={12}>
            <div className={classes.header}>
              <div className={classes.logoContainer}>
                <img
                  src="logoidp.png"
                  alt="BANKDEVX Logo"
                  className={classes.logo}
                  onError={(e) => {
                    e.currentTarget.onerror = null;
                    e.currentTarget.src = 'logo192.png';
                  }}
                />
                <div>
                  <Typography variant="h4" component="h1" className={classes.title}>
                    Internal Developer Portal
                  </Typography>
                  <Typography variant="subtitle1" className={classes.subtitle}>
                    Corporate Links panel to access business resources and development tools
                  </Typography>
                </div>
              </div>
            </div>
          </Grid>
        </Grid>

        {/* Announcements Banner */}
        {/* <Grid container spacing={4}>
          <Grid item xs={12}>
            <AnnouncementBannerWithEmptyState />
          </Grid>
        </Grid> */}

        <Grid container spacing={4}>
          {/* Featured Items */}
          <Grid item xs={12} md={6}>
            <InfoCard title="Featured Items" className={classes.card}>
              <div className={classes.cardContent}>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  Quick access to your most used components and services
                </Typography>
                <HomePageStarredEntities />
              </div>
            </InfoCard>
          </Grid>

          {/* Main Resources */}
          <Grid item xs={12} md={6}>
            <InfoCard title="Main Resources" className={classes.card}>
              <div className={classes.cardContent}>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  Quick access to the most important sections
                </Typography>
                <div style={{ marginTop: 16 }}>
                  <Link to="/catalog" className={classes.actionLink}>
                    <Typography variant="subtitle2">📦 Component Catalog</Typography>
                    <Typography variant="body2" color="textSecondary">
                      Browse Catalog services and components
                    </Typography>
                  </Link>
                  
                  <Link to="/docs" className={classes.actionLink}>
                    <Typography variant="subtitle2">📚 Documentation</Typography>
                    <Typography variant="body2" color="textSecondary">
                      Technical guides and references
                    </Typography>
                  </Link>
                  
                  <Link to="/api-docs" className={classes.actionLink}>
                    <Typography variant="subtitle2">🔌 APIs</Typography>
                    <Typography variant="body2" color="textSecondary">
                      Available APIs
                    </Typography>
                  </Link>
                </div>
              </div>
            </InfoCard>
          </Grid>

          {/* Quick Actions */}
          <Grid item xs={12} md={6}>
            <InfoCard title="Quick Actions" className={classes.card}>
              <div className={classes.cardContent}>
                <div className={classes.quickActions}>
                  <Link to="/catalog" className={classes.actionLink}>
                    <Typography variant="subtitle2">📦 Explore Catalog</Typography>
                    <Typography variant="body2" color="textSecondary">
                      Component Catalog, services, and APIs
                    </Typography>
                  </Link>
                  
                  <Link to="/create" className={classes.actionLink}>
                    <Typography variant="subtitle2">🚀 Create Component</Typography>
                    <Typography variant="body2" color="textSecondary">
                      Use templates to create new projects
                    </Typography>
                  </Link>
                  
                  <Link to="/docs" className={classes.actionLink}>
                    <Typography variant="subtitle2">📚 Documentation</Typography>
                    <Typography variant="body2" color="textSecondary">
                      Technical guides and development resources
                    </Typography>
                  </Link>
                  
                  <Link to="/links" className={classes.actionLink}>
                    <Typography variant="subtitle2">🔗 Corporate Links</Typography>
                    <Typography variant="body2" color="textSecondary">
                      Business tools and resources
                    </Typography>
                  </Link>
                </div>
              </div>
            </InfoCard>
          </Grid>

          {/* Metrics and Status */}
          {/* <Grid item xs={12} md={6}>
            <InfoCard title="System Status" className={classes.card}>
              <div className={classes.cardContent}>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  General information about the platform
                </Typography>
                
                <div style={{ marginTop: 16 }}>
                  <Typography variant="body2">
                    ✅ All services operational
                  </Typography>
                  <Typography variant="body2" style={{ marginTop: 8 }}>
                    🔍 Search available at: <Link to="/search">Search Panel</Link>
                  </Typography>
                  <Typography variant="body2" style={{ marginTop: 8 }}>
                    📢 Announcements: <Link to="/announcements">View Announcements</Link>
                  </Typography>
                  <Typography variant="body2" style={{ marginTop: 8 }}>
                    🔔 Notifications: <Link to="/notifications">Notification Center</Link>
                  </Typography>
                </div>
              </div>
            </InfoCard>
          </Grid> */}
        </Grid>
      </Content>
    </div>
  );
};

export default Dashboard;
